// test-id.service.ts
import { Injectable } from '@angular/core';

/**
 * Dettagli necessari per creare i metadati di un test basato su micro-scenario.
 */
export interface MicroTestMetadataCreationDetails {
  microScenarioId: string;
  macroScenarioId?: string;
  gherkinSection: 'given' | 'when' | 'then'; // Correttamente aggiunto
  testIndex: number;
  source?: string;
}

/**
 * Metadati associati a ciascun test case generato da un micro-scenario.
 */
export interface MicroTestMetadata {
  microScenarioId?: string;
  macroScenarioId?: string;
  gherkinSection: 'given' | 'when' | 'then'; // Correttamente aggiunto
  testIndex: number;
  testId: string;
  displayId: string;
  sequentialId?: number;
  source: string;
  creationTimestamp?: number;
}

/**
 * Struttura di un test case che include i suoi metadati e i dati effettivi del test.
 */
export interface TestWithMetadata {
  metadata: MicroTestMetadata;
  data: any;
}

/**
 * Tipi di test supportati con il loro ordine di priorità
 */
export enum TestType {
  HAPPY_PATH = 'HAPPY_PATH',
  WORKFLOW = 'WORKFLOW',
  EDGE_CASE = 'EDGE_CASE',
  NEGATIVE = 'NEGATIVE',
  BOUNDARY = 'BOUNDARY',
  PERFORMANCE = 'PERFORMANCE',
  SECURITY = 'SECURITY',
  INTEGRATION = 'INTEGRATION',
  REGRESSION = 'REGRESSION',
  ERROR_HANDLING = 'ERROR_HANDLING',
  UI = 'UI',
  OTHER = 'OTHER'
}

@Injectable({
  providedIn: 'root'
})
export class TestIdService {

  /**
   * Ordine di priorità per i tipi di test con fallback dinamico
   */
  private readonly baseTestTypeOrder: Record<string, number> = {
    [TestType.HAPPY_PATH]: 1,
    [TestType.WORKFLOW]: 2,
    [TestType.EDGE_CASE]: 3,
    [TestType.NEGATIVE]: 4,
    [TestType.BOUNDARY]: 5,
    [TestType.PERFORMANCE]: 6,
    [TestType.SECURITY]: 7,
    [TestType.INTEGRATION]: 8,
    [TestType.REGRESSION]: 9,
    [TestType.ERROR_HANDLING]: 10,
    [TestType.UI]: 11,
    [TestType.OTHER]: 99
  };

  constructor() {}

  /**
   * Sorts an array of tests globally by type and then assigns a continuous
   * sequential ID (1, 2, 3...). This is the definitive method for preparing
   * tests for the global state.
   */
  public sortAndSequenceTestsByType(tests: TestWithMetadata[]): TestWithMetadata[] {
    if (!tests || tests.length === 0) {
      return [];
    }

    // 1. Sort all tests globally by their type
    const sortedTests = this.sortTestsByType(tests);

    // 2. Map over the sorted array and assign a new sequential ID
    return sortedTests.map((test, index) => ({
      ...test,
      metadata: {
        ...test.metadata,
        sequentialId: index + 1 // Assign 1, 2, 3...
      }
    }));
  }

  /**
   * Crea l'oggetto MicroTestMetadata basandosi sui dettagli forniti.
   * @param details Oggetto contenente microScenarioId, macroScenarioId (opzionale), gherkinSection e testIndex.
   * @param globalSequentialIndex (Opzionale) ID sequenziale globale se già noto.
   * @returns L'oggetto MicroTestMetadata completo.
   */
  public createTestMetadata(details: MicroTestMetadataCreationDetails, globalSequentialIndex?: number): MicroTestMetadata {
    // Destruttura gherkinSection da details
    const { microScenarioId, macroScenarioId, gherkinSection, testIndex, source } = details;

    const cleanMicroId = microScenarioId
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_-]/g, '')
      .substring(0, 50);

    const generatedTestId = `TC${(testIndex + 1).toString().padStart(2, '0')}`;
    const displayId = `MS-${cleanMicroId}-${generatedTestId}`;
    const effectiveSource = source || 'MicroScenario';

    return {
      microScenarioId,
      macroScenarioId,
      gherkinSection, // <<--- CORREZIONE: Assicura che gherkinSection sia assegnato qui
      testIndex,
      testId: generatedTestId,
      displayId,
      sequentialId: globalSequentialIndex,
      source: effectiveSource,
      creationTimestamp: Date.now(),
    };
  }

  /**
   * Crea un oggetto TestWithMetadata completo, combinando i dati del test con i metadati generati.
   * @param data I dati effettivi del test case.
   * @param details I dettagli per la creazione dei metadati.
   * @param globalSequentialIndex (Opzionale) ID sequenziale globale se già noto.
   * @returns Un oggetto TestWithMetadata.
   */
  public createTestWithMetadata(data: any, details: MicroTestMetadataCreationDetails, globalSequentialIndex?: number): TestWithMetadata {
    return {
      metadata: this.createTestMetadata(details, globalSequentialIndex),
      data
    };
  }

  /**
   * Analizza un displayId (formato MS-xxx-TCxx) per tentare di estrarre i dettagli originali.
   * NOTA: Questa funzione non estrarrà gherkinSection o macroScenarioId dal displayId attuale.
   * @param displayId L'ID stringa da analizzare.
   * @returns Un oggetto parziale MicroTestMetadataCreationDetails o null.
   */
  public parseDisplayId(displayId: string): Omit<MicroTestMetadataCreationDetails, 'source' | 'macroScenarioId' | 'gherkinSection'> | null {
    const match = displayId.match(/^MS-([a-zA-Z0-9_-]+)-TC(\d+)$/);
    if (match) {
      return {
        microScenarioId: match[1],
        testIndex: parseInt(match[2], 10) - 1,
      };
    }
    console.warn(`TestIdService: Could not parse displayId into MicroTestMetadataCreationDetails: ${displayId}`);
    return null;
  }

  /**
   * Filtra i test per uno specifico microScenarioId.
   * @param tests Array di TestWithMetadata da filtrare.
   * @param microScenarioId L'identificatore del micro-scenario.
   * @returns Un array di TestWithMetadata che corrispondono.
   */
  public getMicroScenarioTests(tests: TestWithMetadata[], microScenarioId: string): TestWithMetadata[] {
    return tests.filter(test => test.metadata.microScenarioId === microScenarioId);
  }

  /**
   * Ordina un array di TestWithMetadata basandosi su macroScenarioId (opzionale), gherkinSection (opzionale),
   * microScenarioId e poi su testIndex.
   * Dopo l'ordinamento, aggiorna i sequentialId.
   * @param tests Array di TestWithMetadata da ordinare.
   * @returns Un nuovo array di TestWithMetadata ordinati e con sequentialId aggiornati.
   */
  public sortTests(tests: TestWithMetadata[]): TestWithMetadata[] {
    if (!tests || tests.length === 0) {
      return [];
    }
    const sortedTests = [...tests].sort((a, b) => {
      const metaA = a.metadata;
      const metaB = b.metadata;

      // 0. Ordina per sequentialId (prioritario se presente)
      if (metaA.sequentialId && metaB.sequentialId) {
        if (metaA.sequentialId !== metaB.sequentialId) {
          return metaA.sequentialId - metaB.sequentialId;
        }
      } else if (metaA.sequentialId) {
        return -1; // a has sequentialId, b doesn't, a comes first
      } else if (metaB.sequentialId) {
        return 1; // b has sequentialId, a doesn't, b comes first
      }

      // 1. Ordina per macroScenarioId (if present)
      if (metaA.macroScenarioId && metaB.macroScenarioId) {
        if (metaA.macroScenarioId !== metaB.macroScenarioId) {
          return metaA.macroScenarioId.localeCompare(metaB.macroScenarioId);
        }
      } else if (metaA.macroScenarioId) {
        return -1;
      } else if (metaB.macroScenarioId) {
        return 1;
      }

      // 2. Ordina per gherkinSection (given, when, then)
      // Questo assicura un raggruppamento logico se i test sono mescolati
      const sectionOrder = { 'given': 1, 'when': 2, 'then': 3 };
      const orderA = sectionOrder[metaA.gherkinSection] || 99; // Se gherkinSection non è definito, va in fondo
      const orderB = sectionOrder[metaB.gherkinSection] || 99;
      if (orderA !== orderB) {
        return orderA - orderB;
      }

      // 3. Ordina per microScenarioId
      if (metaA.microScenarioId !== metaB.microScenarioId) {
        return metaA.microScenarioId.localeCompare(metaB.microScenarioId);
      }

      // 4. If same microScenarioId, sort by creationTimestamp (ascending)
      if (metaA.creationTimestamp && metaB.creationTimestamp) {
        return metaA.creationTimestamp - metaB.creationTimestamp;
      } else if (metaA.creationTimestamp) {
        return -1; // a has timestamp, b doesn't, a comes first (older)
      } else if (metaB.creationTimestamp) {
        return 1; // b has timestamp, a doesn't, b comes first (older)
      }

      // 5. If creationTimestamp is same or one/both undefined, sort by testIndex (original order within batch - as a tie-breaker)
      return metaA.testIndex - metaB.testIndex;
    });

    // Note: We don't need to call this.updateSequentialIds(sortedTests) here anymore.
    // The sequentialId will be set by the modal save logic.
    // The `updateSequentialIds` method is now less critical for the main sorting flow
    // but can be kept if needed elsewhere.
    return sortedTests; // Return the sorted array directly
  }

  /**
   * Aggiorna (o aggiunge) un ID sequenziale globale a ciascun test nell'array fornito.
   * @param tests Array di TestWithMetadata.
   * @returns Un nuovo array di TestWithMetadata con i sequentialId aggiornati.
   */
  public updateSequentialIds(tests: TestWithMetadata[]): TestWithMetadata[] {
    if (!tests) {
      return [];
    }
    return tests.map((test, index) => ({
      ...test,
      metadata: {
        ...test.metadata,
        sequentialId: index + 1
      }
    }));
  }

  /**
   * Estrae il tipo di test dalla colonna TYPE con gestione resiliente
   */
  private getTestType(test: TestWithMetadata): string {
    const typeValue = test.data?.TYPE;
    if (!typeValue || typeof typeValue !== 'string') {
      return TestType.OTHER;
    }

    const normalizedType = typeValue.toUpperCase().trim();

    // Check if it's one of our known types
    if (Object.values(TestType).includes(normalizedType as TestType)) {
      return normalizedType;
    }

    // If it's an unknown type, return it as-is (we'll handle it dynamically)
    return normalizedType;
  }

  /**
   * Ottiene l'ordine numerico per un tipo di test con gestione dinamica dei tipi sconosciuti
   */
  private getTestTypeOrder(testType: string): number {
    // Check if we have a predefined order for this type
    if (this.baseTestTypeOrder[testType] !== undefined) {
      return this.baseTestTypeOrder[testType];
    }

    // For unknown types, assign a dynamic order between known types and OTHER
    // This ensures unknown types are sorted alphabetically after known types but before OTHER
    const dynamicOrder = 50; // Place unknown types in the middle
    return dynamicOrder;
  }

  /**
   * Ottiene tutti i tipi di test unici presenti nei dati con ordinamento dinamico
   */
  private getAllTestTypesWithOrder(tests: TestWithMetadata[]): Array<{type: string, order: number}> {
    const allTypes = new Set<string>();

    tests.forEach(test => {
      const testType = this.getTestType(test);
      allTypes.add(testType);
    });

    // Convert to array with orders, handling unknown types
    return Array.from(allTypes).map(type => {
      let order = this.getTestTypeOrder(type);

      // If it's not a known type, create a dynamic order based on alphabetical sorting
      if (!this.baseTestTypeOrder[type] && type !== TestType.OTHER) {
        // Sort unknown types alphabetically among themselves
        const unknownTypes = Array.from(allTypes).filter(t =>
          !this.baseTestTypeOrder[t] && t !== TestType.OTHER
        ).sort();

        const indexInUnknown = unknownTypes.indexOf(type);
        order = 50 + indexInUnknown; // Start at 50 and increment for each unknown type
      }

      return { type, order };
    }).sort((a, b) => a.order - b.order);
  }

  /**
   * Ordina i test per tipo con gestione resiliente di tipi sconosciuti
   */
  public sortTestsByType(tests: TestWithMetadata[]): TestWithMetadata[] {
    if (!tests || tests.length === 0) {
      return [];
    }

    // console.log('Sorting tests by type...');

    // Get all unique test types with their dynamic ordering
    const typesWithOrder = this.getAllTestTypesWithOrder(tests);
    // console.log('Test types found with order:', typesWithOrder);

    return [...tests].sort((a, b) => {
      const testTypeA = this.getTestType(a);
      const testTypeB = this.getTestType(b);
      const typeOrderA = this.getTestTypeOrder(testTypeA);
      const typeOrderB = this.getTestTypeOrder(testTypeB);

      // console.log(`Comparing: ${testTypeA}(${typeOrderA}) vs ${testTypeB}(${typeOrderB})`);

      if (typeOrderA !== typeOrderB) {
        return typeOrderA - typeOrderB;
      }

      // If same type order, sort alphabetically by type name
      if (testTypeA !== testTypeB) {
        return testTypeA.localeCompare(testTypeB);
      }

      // If same type, sort by timestamp or index
      const metaA = a.metadata;
      const metaB = b.metadata;

      if (metaA.creationTimestamp && metaB.creationTimestamp) {
        return metaA.creationTimestamp - metaB.creationTimestamp;
      }

      return metaA.testIndex - metaB.testIndex;
    });
  }

  /**
   * Ordina i test all'interno di ogni gruppo per tipo con gestione resiliente
   */
  public sortTestsByTypeWithinGroups(groupedTests: { macro: any; tests: TestWithMetadata[] }[]): { macro: any; tests: TestWithMetadata[] }[] {
    return groupedTests.map(group => {
      // console.log(`Sorting group: ${group.macro.scenario_title || group.macro.id}`);
      return {
        ...group,
        tests: this.sortTestsByType(group.tests)
      };
    });
  }

  /**
   * Ottiene statistiche sui tipi di test con gestione di tipi dinamici
   */
  public getTestTypeStatistics(tests: TestWithMetadata[]): Record<string, number> {
    const stats: Record<string, number> = {};

    tests.forEach(test => {
      const testType = this.getTestType(test);
      stats[testType] = (stats[testType] || 0) + 1;
    });

    // Sort the stats by the type order for consistent display
    const sortedStats: Record<string, number> = {};
    const typesWithOrder = this.getAllTestTypesWithOrder(tests);

    typesWithOrder.forEach(({type}) => {
      if (stats[type]) {
        sortedStats[type] = stats[type];
      }
    });

    return sortedStats;
  }
}
